import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, TrendingUp, Target, Clock, Award } from "lucide-react";
import { IMAGES } from "../lib/constants";

const HEADLINE_STATS = [
  { value: "₹168 Cr", label: "Annual revenue opportunity unlocked", icon: TrendingUp },
  { value: "40–50%", label: "OPEX reduction via variable model", icon: Target },
  { value: "1–2 Yrs", label: "Breakeven vs 3–4 yrs traditional", icon: Clock },
  { value: "2×", label: "Field force productivity vs peers", icon: Award },
];

const CASE_DATA = [
  {
    eyebrow: "The Client",
    title: "Top-10 Indian Pharma Division",
    body: "A large, well-established division where 70% of the brand portfolio was classified as mature or tail-end. Despite strong legacy equity, these brands received <10% of the total field-force attention — leading to stagnation in urban markets and near-invisibility in extra-urban India.",
  },
  {
    eyebrow: "The Challenge",
    title: "A ₹168 Cr Blind Spot",
    body: "Internal analysis showed the mature & tail-end portfolio had substantial untapped potential — particularly in Tier 2–4 cities — but the traditional urban-heavy sales structure could not economically service those markets. Any new in-house field force would carry 3–4 years of sunk costs before breakeven.",
  },
  {
    eyebrow: "The Solution",
    title: "3D Delta Field Force Model",
    body: "Medstry deployed a dedicated Delta Field Force — young pharmacy graduates from Tier 2–3 cities — mapped directly to high-potential extra-urban markets. HCP Connect, Digital Connect, and Channel Connect were activated in parallel, all under a performance-based revenue-share commercial structure.",
  },
  {
    eyebrow: "The Outcome",
    title: "Mature & Tail-End Portfolio → Growth Engine",
    body: "Within 18 months, the previously neglected brands became the division's fastest-growing cohort. Availability in target chemists crossed 90%+, prescription growth turned positive across the portfolio, and the client realized 40–50% OPEX savings vs a traditional in-house field build — with breakeven in 1–2 years rather than the usual 3–4.",
  },
];

export default function Impact() {
  return (
    <main className="pt-32 md:pt-40 pb-24" data-testid="impact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 mb-10">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-20 md:mb-24">
          <div className="lg:col-span-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Real Impact</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[0.98] text-slate-900">
              Revitalizing a <br />
              <span className="font-editorial italic font-medium text-slate-700">stagnant portfolio.</span>
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              A Top-10 Indian pharma division. 70% mature & tail-end portfolio. Near-zero extra-urban coverage.
              What happened in 18 months.
            </p>
          </div>
        </div>

        {/* Headline stats */}
        <div className="bg-[#0B0F19] text-white p-10 md:p-16 mb-20 md:mb-24 grid grid-cols-2 md:grid-cols-4 gap-10" data-testid="impact-headline-stats">
          {HEADLINE_STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Icon className="h-6 w-6 text-teal-300 mb-5" strokeWidth={1.5} />
                <div className="font-display text-3xl md:text-4xl font-extrabold tnum">{s.value}</div>
                <div className="text-xs text-slate-400 mt-2 leading-snug">{s.label}</div>
              </motion.div>
            );
          })}
        </div>

        {/* Hero image */}
        <div className="mb-20 md:mb-24">
          <img
            src={IMAGES.facility}
            alt="Modern pharmaceutical facility"
            className="w-full aspect-[21/9] object-cover bg-slate-200"
          />
        </div>

        {/* Case narrative */}
        <section className="mb-24" data-testid="impact-case-narrative">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">
              Case Snapshot
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 leading-[1.04]">
              The anatomy of <br />
              <span className="font-editorial italic font-medium text-slate-700">a turnaround.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200">
            {CASE_DATA.map((c, i) => (
              <motion.div
                key={c.eyebrow}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                data-testid={`impact-case-${i}`}
                className="bg-white p-8 md:p-10"
              >
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-700 mb-3">
                  {c.eyebrow}
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-snug">
                  {c.title}
                </h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Quote */}
        <blockquote className="max-w-4xl mx-auto text-center mb-24">
          <div className="font-editorial italic text-2xl md:text-4xl leading-snug text-slate-800">
            "Every mature and tail-end brand deserves dedicated attention and strategic investment.
            We make it economically viable through innovative talent deployment and performance-based economics."
          </div>
          <div className="mt-6 text-sm font-semibold uppercase tracking-[0.15em] text-slate-500">
            Inderjit Sood · Co-Founder & Managing Director
          </div>
        </blockquote>

        {/* CTA */}
        <section className="bg-slate-50 border border-slate-200 p-10 md:p-16 text-center">
          <h3 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 max-w-2xl mx-auto">
            Your case brief, prepared by our experts.
          </h3>
          <p className="text-slate-600 max-w-2xl mx-auto mb-6">
            Request a detailed, confidential portfolio analysis. Within 24 hours we'll identify the
            untapped revenue opportunity hiding in your own brand catalog.
          </p>
          <Link
            to="/#contact"
            data-testid="impact-cta-analysis"
            className="inline-flex items-center gap-2 btn-primary px-6 py-3.5 text-sm font-semibold"
          >
            Request Portfolio Analysis <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </main>
  );
}
